import { useState } from 'react'
import { analytics, EVENTS } from '../../lib/analytics'
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
  Divider,
  Chip,
} from '@mui/material'
import { Visibility, VisibilityOff, ArrowForward, ShieldOutlined } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useLogin } from '../../hooks/useAuth'
import { useTranslation } from 'react-i18next'

export default function LoginPage() {
  const navigate = useNavigate()
  const login = useLogin()
  const { t } = useTranslation()
  const [form, setForm] = useState({ email: '', password: '' })
  const [showPw, setShowPw] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await login.mutateAsync(form)
      navigate('/dashboard')
    } catch {
      analytics.track(EVENTS.SESSION_LOGIN_FAILED, { email: form.email })
    }
  }

  return (
    <Card sx={{ width: '100%', maxWidth: 460, minWidth: 0, overflow: 'hidden', boxShadow: { xs: 'none', sm: '0 26px 70px rgba(35,65,96,.12)' } }}>
      <CardContent sx={{ minWidth: 0, p: { xs: 3, sm: 5 } }}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: { xs: 'flex', lg: 'none' }, alignItems: 'center', gap: 1.25, mb: 5 }}>
            <Box component="img" src="/omnitrace_logo.png" alt="OmniTrace" sx={{ width: 42, height: 42 }} />
            <Typography variant="h6" color="primary.main">OmniTrace</Typography>
          </Box>
          <Chip icon={<ShieldOutlined />} label={t('auth.secureWorkspace')} size="small" color="primary" variant="outlined" sx={{ mb: 2 }} />
          <Typography variant="h4">{t('auth.title')}</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            {t('auth.subtitle')}
          </Typography>
        </Box>

        <Divider sx={{ mb: 3.5 }} />

        {login.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {(login.error as { response?: { data?: { error?: string } } })?.response?.data
              ?.error ?? t('auth.invalidCredentials')}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label={t('auth.email')}
            type="email"
            value={form.email}
            onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
            required
            fullWidth
            autoFocus
            autoComplete="username"
          />
          <TextField
            label={t('auth.password')}
            type={showPw ? 'text' : 'password'}
            value={form.password}
            onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
            required
            fullWidth
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPw((p) => !p)} edge="end">
                    {showPw ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            loading={login.isPending}
            endIcon={<ArrowForward />}
            sx={{ mt: 1, minHeight: 50 }}
          >
            {t('auth.signIn')}
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 3 }}>
          {t('auth.protectedAccess')}
        </Typography>
      </CardContent>
    </Card>
  )
}
